import { useMemo, useState } from "react";
import StatusPill from "./StatusPill";
import { ChevronDown, ChevronUp, ArrowUpDown, RotateCw } from "lucide-react";
import { formatLastFirst } from "@/lib/utils";

const FILTERS = ["All", "Pending", "Present", "Late", "Late Arrival", "Absent", "Signed Out"];

export default function AttendanceTable({ records, onPersonRefresh }) {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("full_name");
  const [sortDir, setSortDir] = useState("asc");
  const [expanded, setExpanded] = useState({});

  const counts = useMemo(() => {
    const c = { All: records.length, Pending: 0, Present: 0, Late: 0, "Late Arrival": 0, Absent: 0, "Signed Out": 0 };
    records.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [records]);

  const filtered = useMemo(() => {
    let rs = records;
    if (filter !== "All") rs = rs.filter((r) => r.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rs = rs.filter((r) => (r.full_name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    rs = [...rs].sort((a, b) => {
      if (sortBy === "check_in_time" || sortBy === "sign_out_time") {
        const an = a[sortBy] || 0; const bn = b[sortBy] || 0;
        return (an - bn) * dir;
      }
      const av = (sortBy === "full_name"
        ? formatLastFirst(a.full_name)
        : (a[sortBy] || "").toString()).toLowerCase();
      const bv = (sortBy === "full_name"
        ? formatLastFirst(b.full_name)
        : (b[sortBy] || "").toString()).toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return rs;
  }, [records, filter, search, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortHeader = ({ label, col, className = "" }) => (
    <button
      onClick={() => toggleSort(col)}
      className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 hover:text-slate-900 ${className}`}
      data-testid={`sort-${col}`}
    >
      {label}
      <ArrowUpDown className={`w-3 h-3 ${sortBy === col ? "text-blue-600" : ""}`} />
    </button>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="attendance-table">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between p-4 border-b border-slate-200">
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition whitespace-nowrap border ${
                filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {f} <span className="ml-1 opacity-70 tabular-nums">{counts[f] ?? 0}</span>
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          className="w-full md:w-72 px-3 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-blue-400 text-sm"
          data-testid="search-input"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <th className="text-left px-3 sm:px-4 py-3 sticky left-0 bg-slate-50"><SortHeader label="Name" col="full_name" /></th>
              <th className="text-left px-3 sm:px-4 py-3 bg-slate-50"><SortHeader label="Status" col="status" /></th>
              <th className="text-left px-3 sm:px-4 py-3"><SortHeader label="Sign-In" col="check_in_time" /></th>
              <th className="text-left px-3 sm:px-4 py-3 hidden md:table-cell"><SortHeader label="Sign-Out" col="sign_out_time" /></th>
              <th className="text-left px-3 sm:px-4 py-3 hidden lg:table-cell"><SortHeader label="Visit Type" col="visit_type" /></th>
              <th className="text-left px-3 sm:px-4 py-3 hidden lg:table-cell"><SortHeader label="Device" col="device_name" /></th>
              <th className="px-2 py-3 w-8" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="table-empty">
                  No staff match this filter.
                </td>
              </tr>
            )}
            {filtered.map((r) => {
              const isOpen = !!expanded[r.member_id];
              return (
                <tr key={r.member_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`row-${r.member_id}`}>
                  <td className="px-3 sm:px-4 py-3 align-top">
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [r.member_id]: !isOpen }))}
                      className="flex items-start gap-2 text-left w-full"
                      data-testid={`expand-${r.member_id}`}
                    >
                      {isOpen ? <ChevronUp className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />}
                      {r.photo_thumb_base64 ? (
                        <img
                          src={`data:image/jpeg;base64,${r.photo_thumb_base64}`}
                          alt=""
                          className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
                          data-testid={`row-avatar-${r.crew_id || r.member_id}`}
                        />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-slate-400 font-bold text-[11px]"
                          aria-hidden="true"
                        >
                          {(r.full_name || "?")
                            .split(" ")
                            .map((p) => p[0])
                            .filter(Boolean)
                            .slice(0, 2)
                            .join("")
                            .toUpperCase()}
                        </div>
                      )}
                      <span className="min-w-0">
                        <div className="font-semibold text-slate-900 break-words">{formatLastFirst(r.full_name) || "—"}</div>
                        <div className="text-xs text-slate-500 break-all hidden sm:block">{r.email || ""}</div>
                        {/* On mobile: sign-out + visit type fold into the name cell */}
                        <div className="md:hidden text-xs text-slate-500 mt-1 tabular-nums">
                          {r.sign_out_local_display && <span>out {r.sign_out_local_display}</span>}
                          {r.visit_type && r.sign_out_local_display && <span className="mx-1">·</span>}
                          {r.visit_type && <span>{r.visit_type}</span>}
                        </div>
                        {isOpen && (
                          <div className="mt-2 text-xs text-slate-500 space-y-0.5 font-mono break-all">
                            {r.email && <div className="sm:hidden">{r.email}</div>}
                            {r.visit_id && <div>visit_id: {r.visit_id}</div>}
                            {r.check_in_time && <div>check_in: {r.check_in_time}</div>}
                            {r.sign_out_time && <div>sign_out: {r.sign_out_time}</div>}
                            <div>locked: {String(!!r.is_locked)}</div>
                            <button
                              onClick={(e) => { e.stopPropagation(); onPersonRefresh && onPersonRefresh(r); }}
                              className="mt-2 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-sans"
                              data-testid={`refresh-person-${r.member_id}`}
                            >
                              <RotateCw className="w-3 h-3" /> Refresh data
                            </button>
                          </div>
                        )}
                      </span>
                    </button>
                  </td>
                  <td className="px-3 sm:px-4 py-3 align-top"><StatusPill status={r.status} prior={r.prior_status} /></td>
                  <td className="px-3 sm:px-4 py-3 align-top tabular-nums text-slate-700 whitespace-nowrap">{r.check_in_local_display || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 sm:px-4 py-3 align-top tabular-nums text-slate-700 hidden md:table-cell whitespace-nowrap">{r.sign_out_local_display || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 sm:px-4 py-3 align-top text-slate-600 hidden lg:table-cell">{r.visit_type || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 sm:px-4 py-3 align-top text-slate-600 hidden lg:table-cell">{r.device_name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-3" />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
